import { useEffect, useState } from "react";
import { Search, Phone, Mail, RefreshCw, CheckCircle } from "lucide-react";
import LeadDrawer from "../components/leads/LeadDrawer";

const sourceIcons = { facebook_ads:"📘", facebook_ad:"📘", whatsapp:"💬", phone_call:"📞", website:"🌐", referral:"🤝", manual:"✍️", other:"📌" };

function getToken() { return localStorage.getItem("token"); }

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = () => {
    setLoading(true);
    fetch("/api/leads", {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => res.json())
      .then(data => {
        setLeads(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleFbSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch("/api/fbsyncleads", {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setSyncResult(data);
      if (data.success) load();
    } catch {
      setSyncResult({ error: "Network error. Please try again." });
    }
    setSyncing(false);
  };

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !search || [l.name, l.email, l.phone, l.company].some(v => v?.toLowerCase().includes(q));
    return matchSearch
      && (stageFilter === "all" || l.stage === stageFilter)
      && (sourceFilter === "all" || l.source === sourceFilter);
  });

  // Build a clean sync summary — only show forms that had new leads
  const renderSyncResult = () => {
    if (!syncResult) return null;
    if (syncResult.error) {
      return (
        <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 8, fontSize: 13,
          background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>
          ❌ {syncResult.error}
        </div>
      );
    }
    const activeForms = syncResult.forms?.filter(f => f.imported > 0) || [];
    return (
      <div style={{ marginBottom: 16, padding: "14px 16px", borderRadius: 8, fontSize: 13,
        background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: activeForms.length ? 8 : 0 }}>
          <CheckCircle size={15} color="#16a34a" />
          <span style={{ color: "#16a34a", fontWeight: 600 }}>
            {syncResult.total_imported === 0
              ? "Already up to date — no new leads found"
              : `${syncResult.total_imported} new lead${syncResult.total_imported !== 1 ? "s" : ""} imported from Facebook`}
          </span>
          {syncResult.total_skipped > 0 && (
            <span style={{ color: "#94a3b8", fontSize: 12 }}>
              ({syncResult.total_skipped} duplicate{syncResult.total_skipped !== 1 ? "s" : ""} skipped)
            </span>
          )}
        </div>
        {activeForms.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            {activeForms.map(f => (
              <span key={f.form_id} style={{ background: "#dcfce7", color: "#166534",
                padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
                {f.form_name}: {f.imported} new
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">Leads</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-secondary" onClick={handleFbSync} disabled={syncing}
            style={{ gap: 6, display: "flex", alignItems: "center" }} title="Import leads from Facebook">
            <RefreshCw size={14} style={{ animation: syncing ? "spin 0.8s linear infinite" : "none" }} />
            {syncing ? "Syncing..." : "Sync Facebook"}
          </button>
          <button className="btn btn-primary" onClick={() => { setSelectedLead(null); setDrawerOpen(true); }}>
            + New Lead
          </button>
        </div>
      </div>

      {renderSyncResult()}

      <div className="filters-row">
        <div className="search-bar">
          <Search className="search-icon" />
          <input className="form-input" placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 150 }} value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
          <option value="all">All Stages</option>
          {["new","contacted","qualified","proposal","negotiation","won","lost"].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select className="form-select" style={{ width: 160 }} value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
          <option value="all">All Sources</option>
          {["facebook_ad","whatsapp","phone_call","website","referral","manual","other"].map(s => (
            <option key={s} value={s}>{s.replace(/_/g," ")}</option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? (
            <p style={{ padding: 20, color: "#94a3b8" }}>Loading leads...</p>
          ) : filtered.length === 0 ? (
            <p style={{ padding: 20, color: "#94a3b8" }}>No leads found.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Contact</th><th>Source</th><th>Stage</th><th>Value</th><th>Assigned To</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(lead => (
                  <tr key={lead.id} onClick={() => { setSelectedLead(lead); setDrawerOpen(true); }}>
                    <td className="td-name">
                      <strong>{lead.name}</strong>
                      {lead.company && <span>{lead.company}</span>}
                    </td>
                    <td className="td-contact">
                      {lead.email && <div style={{ display:"flex", alignItems:"center", gap:4 }}><Mail size={11}/>{lead.email}</div>}
                      {lead.phone && <div style={{ display:"flex", alignItems:"center", gap:4 }}><Phone size={11}/>{lead.phone}</div>}
                    </td>
                    <td>{sourceIcons[lead.source] ?? "📌"} <span style={{ fontSize:12, color:"#64748b" }}>{lead.source?.replace(/_/g," ")}</span></td>
                    <td><span className={`badge badge-${lead.stage}`}>{lead.stage}</span></td>
                    <td style={{ fontWeight:500 }}>{lead.value ? `$${lead.value.toLocaleString()}` : "—"}</td>
                    <td style={{ color:"#64748b", fontSize:12 }}>{lead.assigned_to || "Unassigned"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <LeadDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} lead={selectedLead} onSaved={load} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}